<script setup lang="ts">
import EstateRibbons from '../shared/EstateRibbons.vue'
import EstateFavoriteTriggerVue from '../shared/EstateFavoriteTrigger.vue'
import EstateThumbGallery from './EstateThumbGallery.vue'
import EstateThumbContent from './EstateThumbContent.vue'
import EstateThumbContactPerson from './EstateThumbContactPerson.vue'
import type { EstateListing } from '~/types/api/EstateListing'
import type { EstateType } from '~/types/EstateType'

const props = defineProps<{
  estateData: EstateListing
  estateType: EstateType
}>()

const estateLocaleLink = getEstateDetailLink({
  estateType: props.estateType,
  slug: props.estateData.slug,
  id: props.estateData.id,
})
</script>

<template>
  <article class="c-estate-thumb-horizontal">
    <EstateRibbons
      :estate-data="estateData"
      class="c-estate-thumb-horizontal__ribbons"
      size="small"
    />
    <EstateFavoriteTriggerVue
      :estate-data="estateData"
      class="c-estate-thumb-horizontal__favorite"
    />

    <div class="c-estate-thumb-horizontal__gallery">
      <EstateThumbGallery
        :estate-data="estateData"
        thumb-type="listing-horizontal"
      />
    </div>

    <div class="c-estate-thumb-horizontal__contact-person">
      <EstateThumbContactPerson :contact-person="estateData.contact_person" />
    </div>

    <div class="c-estate-thumb-horizontal__content">
      <EstateThumbContent
        :estate-locale-link="estateLocaleLink"
        :estate-data="estateData"
      />
    </div>
  </article>
</template>

<style lang="scss" scoped>
@use '@inkline/inkline/css/mixins' as *;

.c-estate-thumb-horizontal {
  --content-border-color: hsla(
    var(--color-dark-h),
    var(--color-dark-s),
    var(--color-dark-l),
    0.16
  );

  width: 100%;
  height: 100%;
  display: grid;
  grid-template:
    'gallery content' auto
    / 240px 1fr;
  transition: all var(--transition-duration) var(--transition-timing-function);
  position: relative;

  @include breakpoint-up('lg') {
    &:hover {
      --content-border-color: hsla(
        var(--color-dark-h),
        var(--color-dark-s),
        var(--color-dark-l),
        0.08
      );

      border-radius: 6px;
      border-color: transparent;
      box-shadow: 0 0 8px 0
        hsla(
          var(--color-dark-h),
          var(--color-dark-s),
          var(--color-dark-l),
          0.08
        );

      :deep(.c-estate-thumb-horizontal-gallery__arrows) {
        --estate-thumb-gallery--arrows--opacity: 1;
      }
    }
  }

  @include breakpoint-down('xl') {
    grid-template-columns: 200px 1fr;
  }

  &__ribbons {
    grid-area: gallery;
    position: absolute;
    top: 10px;
    left: 10px;
    z-index: 2;
  }

  &__favorite {
    grid-area: gallery;
    position: absolute;
    top: 4px;
    right: 4px;
    z-index: 2;
  }

  &__gallery {
    grid-area: gallery;
    width: 100%;
    aspect-ratio: 5 / 3;
    flex: 0 0 auto;
    min-height: 0;
    height: 100%;
    overflow: hidden;
    border-top-left-radius: inherit;
    border-bottom-left-radius: inherit;
  }

  &__contact-person {
    position: absolute;
    top: 16px;
    right: 20px;

    :deep(.c-estate-thumb-contact-person) {
      display: block;
      position: static;

      img {
        border: none;
        width: 36px;
        height: 36px;
      }
    }
  }

  &__content {
    display: flex;
    align-items: center;
    border-top-right-radius: inherit;
    border-bottom-right-radius: inherit;
    grid-area: content;
    padding: 18px 28px;
    transition: border-color var(--transition-duration)
      var(--transition-timing-function);
    border: 1px solid var(--content-border-color);

    @include breakpoint-down('md') {
      padding: 16px 24px;
    }

    :deep(.c-estate-thumb-content) {
      --c-estate-thumb-content--title--font-size: 22px;
      --c-estate-thumb-content--title--margin-bottom: 2px;
      --c-estate-thumb-content--title--padding-right: 42px;
      --c-estate-thumb-content--address--font-size: 15px;
      --c-estate-thumb-content--address--margin-bottom: 18px;
      --c-estate-thumb-content--address--padding-right: 42px;

      @include breakpoint-down('xl') {
        --c-estate-thumb-content--address--margin-bottom: 12px;
      }

      .c-estate-thumb-parameters {
        @include breakpoint-down('xl') {
          --estate-thumb-parameters--image-size: 20px;

          gap: 10px 16px;

          .sc-content {
            font-size: 15px;

            span {
              display: none;
            }
          }
        }
      }
    }
  }
}
</style>
